import type { ItemAgirHoje, LinhaDaFila, ResumoDaSemana, Situacao, VisaoGeral } from "@base/contracts";
import { Botao, CampoDeSelecao, CampoDeTexto, EstadoCarregando, EstadoDeErro, EstadoVazio, cn } from "@base/ui";
import { ChevronDown, CircleCheck } from "lucide-react";
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { formatar } from "../../dashboard";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { navegar, usarParametro } from "../../../shared/navegacao/navegacao";
import {
  periodoDaUrl,
  type PeriodoDaCentral,
  useAcessosDaCentral,
  useAutoresAutorizados,
  useSalvarAutores,
} from "../application/central.contexto";
import { caminhoDaConta } from "./pagina-da-conta";
import { SeloAoVivo } from "./selo-ao-vivo";
import { SeloDaSituacao } from "./selo-da-situacao";
import { tempoRelativo } from "./tempo-relativo";
import {
  LEGENDA,
  ROTULO_DA_PLATAFORMA,
  frase,
  moedaDeCentavos,
  resumoDaFila,
  rotuloDaSituacaoOuFrase,
  rotuloDeQuemAge,
} from "./textos";

const CARTAO = "min-w-0 rounded-xl border border-border bg-card";
const BOTAO_DA_LINHA = "h-11 rounded-lg px-4 font-semibold";

type AcoesDoAlerta = {
  /** Sem estas props a lista segue só leitura — o spec antigo não liga gateway. */
  readonly aoMarcarComoVisto?: ((id: string) => void) | undefined;
  readonly aoResolver?: ((id: string, nota: string) => void) | undefined;
};

function abrirConta(linha: LinhaDaFila) {
  navegar(caminhoDaConta(linha.cliente, linha.campanha, linha.tipoCodigo ?? ""));
}

/** O alerta não traz campanha: abre a de mais reais em jogo do mesmo cliente (a fila já vem nessa ordem). */
function caminhoDoAlerta(item: ItemAgirHoje, fila: readonly LinhaDaFila[]): string {
  const linha = fila.find((itemFila) => itemFila.cliente === item.cliente);
  return caminhoDaConta(item.cliente, linha?.campanha ?? "", linha?.tipoCodigo ?? "");
}

const PERIODOS: readonly { valor: PeriodoDaCentral; rotulo: string }[] = [
  { valor: "dia", rotulo: "Dia" },
  { valor: "semana", rotulo: "Semana" },
  { valor: "mes", rotulo: "Mês" },
];

/** Visão geral da Central. O dado chega pronto: o menu (ou um teste) passa a visão. */
export function PaginaDeCentral({ visao, aoMarcarComoVisto, aoResolver }: { readonly visao: VisaoGeral } & AcoesDoAlerta) {
  const periodo = periodoDaUrl(usarParametro("periodo"));
  const [cliente, setCliente] = useState("");
  const [plataforma, setPlataforma] = useState("");
  const [situacao, setSituacao] = useState<Situacao | "">("");
  const clinicas = useMemo(
    () => [...new Set(visao.fila.map((linha) => linha.cliente))].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [visao.fila],
  );
  const fila = visao.fila.filter((linha) => {
    if (cliente && linha.cliente !== cliente) return false;
    if (situacao && linha.situacao !== situacao) return false;
    if (plataforma) {
      const tipo = linha.tipo.toLowerCase();
      if (plataforma === "meta" && !tipo.startsWith("meta")) return false;
      if (plataforma === "google" && !tipo.startsWith("google")) return false;
      if (plataforma === "kommo" && !/kommo|funil/.test(tipo)) return false;
    }
    return true;
  });
  const agirHoje = visao.agirHoje.filter((item) => {
    if (cliente && item.cliente !== cliente) return false;
    if (plataforma && item.plataforma !== plataforma) return false;
    return true;
  });

  return (
    <div className="flex min-w-0 flex-col gap-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold leading-tight text-foreground">Central</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Período: {visao.periodoRotulo} · atualizado {tempoRelativo(visao.atualizadoEm, Date.now())}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div role="group" aria-label="Período" className="flex gap-1 rounded-[10px] border border-border bg-card p-1">
            {PERIODOS.map((item) => (
              <button
                key={item.valor}
                type="button"
                aria-pressed={periodo === item.valor}
                className={cn(
                  "min-h-10 cursor-pointer rounded-md px-4 text-sm transition-colors duration-200",
                  periodo === item.valor
                    ? "bg-foreground font-semibold text-background"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => navegar(item.valor === "semana" ? "/gestao/central" : `/gestao/central?periodo=${item.valor}`)}
              >
                {item.rotulo}
              </button>
            ))}
          </div>
          <SeloAoVivo />
        </div>
      </div>

      <Resumo resumo={visao.resumo} fila={visao.fila} aoFiltrar={setSituacao} />

      <div className="flex flex-wrap items-end gap-3">
        <CampoDeSelecao
          className="w-56"
          rotulo="Clínica"
          textoVazio="Todas"
          value={cliente}
          opcoes={clinicas.map((nome) => ({ valor: nome, rotulo: nome }))}
          onChange={(evento) => setCliente(evento.target.value)}
        />
        <CampoDeSelecao
          className="w-44"
          rotulo="Plataforma"
          textoVazio="Todas"
          value={plataforma}
          opcoes={[
            { valor: "meta", rotulo: "Meta" },
            { valor: "google", rotulo: "Google" },
            { valor: "kommo", rotulo: "Kommo" },
          ]}
          onChange={(evento) => setPlataforma(evento.target.value)}
        />
        <CampoDeSelecao
          className="w-48"
          rotulo="Situação"
          textoVazio="Todas"
          value={situacao}
          opcoes={[
            { valor: "urgente", rotulo: "Urgente" },
            { valor: "cronico", rotulo: "Crônico" },
            { valor: "aviso-precoce", rotulo: "Aviso precoce" },
            { valor: "pouco-volume", rotulo: "Pouco volume" },
          ]}
          onChange={(evento) => setSituacao((evento.target.value || "") as Situacao | "")}
        />
      </div>

      <Secao id="titulo-agir-hoje" titulo="Agir hoje" descricao="Guardrails conferidos todo dia. Não esperam o dia de otimização.">
        {agirHoje.length === 0 ? (
          <EstadoVazio
            titulo="Nada para agir hoje."
            descricao="Nenhum guardrail aberto no topo da fila."
            icone={<CircleCheck aria-hidden="true" />}
          />
        ) : (
          <ul className="flex flex-col gap-2.5">
            {agirHoje.map((item) => (
              <ItemDeAgirHoje
                key={item.id}
                item={item}
                caminho={caminhoDoAlerta(item, visao.fila)}
                aoMarcarComoVisto={aoMarcarComoVisto}
                aoResolver={aoResolver}
              />
            ))}
          </ul>
        )}
      </Secao>

      <Secao id="titulo-fila" titulo="Fila da otimização" descricao={`Ordenada por reais em jogo · ${visao.periodoRotulo}`}>
        {fila.length === 0 ? (
          <EstadoVazio titulo="Nenhuma conta na fila." descricao="Quando a coleta rodar, as contas aparecem aqui." />
        ) : (
          <Fila linhas={fila} />
        )}
        <div className={cn(CARTAO, "flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 text-sm text-foreground")}>
          <span>
            <strong>{formatar.quantidade(visao.resumo.contasSemAlerta)} contas</strong> sem alerta neste período
          </span>
        </div>
        <p className="max-w-[980px] text-[13px] leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Como ler:</strong>{" "}
          {LEGENDA.map(([termo, texto]) => `${termo} = ${texto}`).join(" ")}
        </p>
      </Secao>

      <RegistroDaCentral />
    </div>
  );
}

function Resumo({
  resumo,
  fila,
  aoFiltrar,
}: {
  readonly resumo: ResumoDaSemana;
  readonly fila: readonly LinhaDaFila[];
  readonly aoFiltrar: (situacao: Situacao | "") => void;
}) {
  const naFila = resumo.urgentes + resumo.cronicos + resumo.avisos + resumo.semVolume;
  const contasAcima = new Set(fila.filter((linha) => linha.emJogoCentavos > 0).map((linha) => linha.cliente)).size;
  return (
    <section aria-label="Resumo da semana" className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
      <Indicador
        rotulo="Em jogo nesta semana"
        valor={moedaDeCentavos(resumo.emJogoCentavos)}
        detalhe={`Gasto acima da meta em ${formatar.quantidade(contasAcima)} ${contasAcima === 1 ? "conta" : "contas"}`}
        aoClicar={() => aoFiltrar("")}
      />
      <Indicador
        rotulo="Agir hoje"
        valor={formatar.quantidade(resumo.guardrailsAbertos)}
        detalhe="Guardrails abertos"
        alerta={resumo.guardrailsAbertos > 0}
        aoClicar={() => document.getElementById("titulo-agir-hoje")?.scrollIntoView({ behavior: "smooth", block: "start" })}
      />
      <Indicador
        rotulo="Fila da otimização"
        valor={formatar.quantidade(naFila)}
        detalhe={resumoDaFila(resumo)}
        aoClicar={() => {
          aoFiltrar("");
          document.getElementById("titulo-fila")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />
      <Indicador
        rotulo="Contas monitoradas"
        valor={formatar.quantidade(resumo.contasMonitoradas)}
        detalhe={`${formatar.quantidade(resumo.contasSemAlerta)} sem nenhum alerta`}
        aoClicar={() => aoFiltrar("")}
      />
    </section>
  );
}

function Indicador({
  rotulo,
  valor,
  detalhe,
  alerta = false,
  aoClicar,
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly detalhe: string;
  readonly alerta?: boolean;
  readonly aoClicar?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      className={cn(CARTAO, "flex cursor-pointer flex-col gap-1.5 px-[22px] py-5 text-left transition-colors duration-200 hover:bg-muted/40")}
    >
      <span className="text-[13px] text-muted-foreground">{rotulo}</span>
      <span className={cn("text-[34px] font-bold leading-[1.1] tabular-nums", alerta ? "text-destructive" : "text-foreground")}>
        {valor}
      </span>
      <span className="text-[13px] text-muted-foreground">{detalhe}</span>
    </button>
  );
}

function Secao({
  id,
  titulo,
  descricao,
  children,
}: {
  readonly id: string;
  readonly titulo: string;
  readonly descricao: string;
  readonly children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="flex min-w-0 flex-col gap-3.5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 id={id} className="text-xl font-bold text-foreground">
          {titulo}
        </h2>
        <span className="text-[13px] text-muted-foreground">{descricao}</span>
      </div>
      {children}
    </section>
  );
}

function ItemDeAgirHoje({
  item,
  caminho,
  aoMarcarComoVisto,
  aoResolver,
}: { readonly item: ItemAgirHoje; readonly caminho: string } & AcoesDoAlerta) {
  const [notaAberta, setNotaAberta] = useState(false);
  const [nota, setNota] = useState("");
  const notaLimpa = nota.trim();

  function confirmar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (notaLimpa.length === 0) return;
    aoResolver?.(item.id, notaLimpa);
    setNota("");
    setNotaAberta(false);
  }

  return (
    <li className={cn(CARTAO, "flex flex-wrap items-center gap-x-5 gap-y-3 px-5 py-4")}>
      <SeloDaSituacao situacao="urgente" rotulo="Crítico" />
      <div className="min-w-0 flex-[1_1_420px]">
        <h3 className="break-words text-[15px] font-semibold text-foreground">
          {item.cliente} · {ROTULO_DA_PLATAFORMA[item.plataforma]} — {frase(item.titulo)}
        </h3>
        <p className="mt-0.5 break-words text-[13px] text-muted-foreground">{rotuloDaSituacaoOuFrase(item.descricao)}</p>
        <p className="mt-0.5 break-words text-[13px] font-medium text-foreground">Ação: {frase(item.acao)}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Botao variante="contorno" className={BOTAO_DA_LINHA} onClick={() => navegar(caminho)}>
          Ver na conta
        </Botao>
        <Botao variante="fantasma" className={BOTAO_DA_LINHA} onClick={() => aoMarcarComoVisto?.(item.id)}>
          Marcar como visto
        </Botao>
        <Botao
          variante="fantasma"
          className={BOTAO_DA_LINHA}
          aria-expanded={notaAberta}
          onClick={() => setNotaAberta((aberta) => !aberta)}
        >
          Resolver
        </Botao>
      </div>
      {notaAberta ? (
        <form className="flex w-full max-w-md flex-col gap-texto" onSubmit={confirmar}>
          <CampoDeTexto rotulo="Nota" value={nota} onChange={(evento) => setNota(evento.target.value)} />
          <Botao type="submit" tamanho="pequeno" className="w-fit cursor-pointer" disabled={notaLimpa.length === 0}>
            Confirmar
          </Botao>
        </form>
      ) : null}
    </li>
  );
}

function emJogoDaLinha(linha: LinhaDaFila): ReactNode {
  if (linha.situacao === "pouco-volume") return <span className="font-normal text-muted-foreground">Ainda não julga</span>;
  if (linha.emJogoCentavos === 0) return <span className="font-normal text-muted-foreground">Dentro da meta</span>;
  return linha.emJogoRotulo;
}

function Fila({ linhas }: { readonly linhas: readonly LinhaDaFila[] }) {
  return (
    <ul aria-label="Fila da otimização" className="flex flex-col gap-2.5">
      {linhas.map((linha, indice) => (
        <li key={`${linha.cliente}|${linha.campanha}|${linha.tipo}`}>
          <article
            className={cn(
              CARTAO,
              "flex cursor-pointer flex-col gap-3 px-5 py-4 transition-colors duration-200 hover:bg-muted/40",
            )}
            onClick={() => abrirConta(linha)}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs tabular-nums text-muted-foreground">{indice + 1}</p>
                <h3 className="break-words text-[15px] font-semibold text-foreground">{linha.cliente}</h3>
                <p className="mt-0.5 break-words text-[13px] text-muted-foreground">
                  {linha.tipo} · {linha.campanhas === 1 ? linha.campanha : `${linha.campanhas} campanhas`}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <SeloDaSituacao situacao={linha.situacao} />
                <Botao
                  variante={linha.destaque ? "primario" : "contorno"}
                  className="h-11 rounded-lg px-3.5 text-[13px] font-semibold"
                  aria-label={`Abrir ${linha.cliente}, ${linha.campanha}`}
                  onClick={(evento) => {
                    evento.stopPropagation();
                    abrirConta(linha);
                  }}
                >
                  Abrir
                </Botao>
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
              <DadoDaFila
                termo={linha.metrica}
                valor={linha.atualRotulo}
                detalhe={`Meta ${linha.metaRotulo} · base ${linha.baseRotulo}`}
              />
              <DadoDaFila termo="Causa" valor={frase(linha.causa)} />
              <DadoDaFila termo="Quem age" valor={linha.quemAge ? rotuloDeQuemAge(linha.quemAge) : "—"} />
              <DadoDaFila termo="Em jogo / semana" valor={emJogoDaLinha(linha)} />
            </dl>
            <p className="text-xs text-muted-foreground">{frase(linha.volumeRotulo)}</p>
          </article>
        </li>
      ))}
    </ul>
  );
}

function DadoDaFila({
  termo,
  valor,
  detalhe,
}: {
  readonly termo: string;
  readonly valor: ReactNode;
  readonly detalhe?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{termo}</dt>
      <dd className="break-words font-semibold text-foreground">{valor}</dd>
      {detalhe ? <p className="break-words text-xs text-muted-foreground">{detalhe}</p> : null}
    </div>
  );
}

function RegistroDaCentral() {
  return (
    <details className="group mt-2">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-md py-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:text-foreground motion-reduce:transition-none marker:content-none [&::-webkit-details-marker]:hidden">
        <ChevronDown
          aria-hidden="true"
          className="size-3.5 shrink-0 transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none"
        />
        Registro
      </summary>
      <div className="mt-3 grid gap-6 border-t border-border/60 pt-4 sm:grid-cols-2">
        <AutoresAutorizados />
        <QuemAbriu />
      </div>
    </details>
  );
}

function AutoresAutorizados() {
  const consulta = useAutoresAutorizados();
  const salvar = useSalvarAutores();
  const [locais, setLocais] = useState<string[] | null>(null);
  const [nome, setNome] = useState("");
  const [aviso, setAviso] = useState<string | null>(null);
  const lista = locais ?? consulta.data ?? [];
  const nomeLimpo = nome.trim();

  function acrescentar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (nomeLimpo.length === 0 || lista.includes(nomeLimpo)) return;
    setLocais([...lista, nomeLimpo]);
    setNome("");
    setAviso(null);
  }

  function remover(autor: string) {
    setLocais(lista.filter((item) => item !== autor));
    setAviso(null);
  }

  function gravar() {
    setAviso(null);
    void salvar
      .mutateAsync([...lista])
      .then(() => {
        setLocais(null);
        setAviso("Autores salvos.");
      })
      .catch(() => undefined);
  }

  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      <div className="flex flex-col gap-0.5">
        <h3 id="titulo-autores" className="text-sm font-medium text-foreground">
          Autores autorizados
        </h3>
        <p className="text-xs text-muted-foreground">
          Só dispara alteração não identificada quando esta lista tem alguém.
        </p>
      </div>
      {consulta.isPending ? <EstadoCarregando rotulo="Carregando autores" /> : null}
      {consulta.isError ? (
        <EstadoDeErro mensagem={mensagemDoErro(consulta.error)} aoTentarNovamente={() => void consulta.refetch()} />
      ) : null}
      {consulta.isSuccess ? (
        <>
          {lista.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhum autor autorizado.</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {lista.map((autor) => (
                <li key={autor} className="flex items-center justify-between gap-2 text-sm">
                  <span className="min-w-0 break-words text-foreground">{autor}</span>
                  <Botao
                    variante="fantasma"
                    tamanho="pequeno"
                    className="cursor-pointer"
                    aria-label={`Remover ${autor}`}
                    onClick={() => remover(autor)}
                  >
                    Remover
                  </Botao>
                </li>
              ))}
            </ul>
          )}
          <form className="flex flex-col gap-2 sm:flex-row sm:items-end" onSubmit={acrescentar}>
            <CampoDeTexto
              className="min-w-0 flex-1"
              rotulo="Autor"
              autoComplete="off"
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
            />
            <Botao
              type="submit"
              variante="contorno"
              tamanho="pequeno"
              className="cursor-pointer"
              disabled={nomeLimpo.length === 0}
            >
              Acrescentar
            </Botao>
          </form>
          <div>
            <Botao type="button" tamanho="pequeno" className="cursor-pointer" carregando={salvar.isPending} onClick={gravar}>
              Salvar
            </Botao>
          </div>
          {aviso ? (
            <p role="status" className="text-sm font-medium text-success">
              {aviso}
            </p>
          ) : null}
          {salvar.isError ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {mensagemDoErro(salvar.error)}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function QuemAbriu() {
  const consulta = useAcessosDaCentral();
  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      <h3 id="titulo-quem-abriu" className="text-sm font-medium text-foreground">
        Quem abriu
      </h3>
      {consulta.isPending ? <EstadoCarregando rotulo="Carregando quem abriu" /> : null}
      {consulta.isError ? (
        <EstadoDeErro mensagem={mensagemDoErro(consulta.error)} aoTentarNovamente={() => void consulta.refetch()} />
      ) : null}
      {consulta.isSuccess && consulta.data.length === 0 ? (
        <p className="text-xs text-muted-foreground">Ninguém abriu ainda</p>
      ) : null}
      {consulta.isSuccess && consulta.data.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {consulta.data.map((acesso, indice) => (
            <li
              key={`${acesso.usuarioId}|${acesso.abertoEm}|${indice}`}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs"
            >
              <span className="min-w-0 break-words text-foreground">{acesso.nome}</span>
              <time dateTime={acesso.abertoEm} className="tabular-nums text-muted-foreground">
                {formatar.momento(acesso.abertoEm)}
              </time>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
